// frontend/smartdocs-admin/src/pages/SettingsPage.tsx
import { useEffect } from 'react';
import { useSettings } from '@/context/SettingsContext';
import { toast } from 'react-hot-toast';
import { useLocation } from "react-router-dom";
import { Bell } from 'lucide-react';
import Button from '@shared/Button';

interface SettingsPageProps {
  children?: React.ReactNode;
}

interface SettingPageInfo {
  title: string;
  category: string | null;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ children }) => {
  const {
    loading,
    error,
    hasUnsavedChanges,
    saveSettings,
    fetchSettings
  } = useSettings();
  const location = useLocation();


  const getSettingPageTitle = (): SettingPageInfo => {
    const path =  location.pathname.slice(location.pathname.indexOf('/',2));
    switch (path) {
      case '/general':
        // setCategory('General');
        return {title: 'General System Settings', category: 'General'};
      case '/query':
        // setCategory('Query');
        return {title:'Query and Search Settings', category: 'Query'};
      case '/doc-processing':
        // setCategory('Document Processing');
        return {title:'Document Processing Settings', category: 'DocumentProcessing'};
      case '/notifications':
        // setCategory('Notifications');
        return {title:'Notifications Settings', category: 'Notifications'};
      case '/security':
        // setCategory('Security');
        return {title:'Security Settings', category: 'Security'};
      case '/integrations':
        // setCategory('Integrations');
        return {title:'Integrations Settings', category: 'Integrations'};
      case '/advanced':
        // setCategory('Advanced');
        return {title:'Advanced System Configurations', category: 'Advanced'};
      case '/system-health':
        return {title:'System Health Dashboard', category: null};
      default:
        // setCategory(null);
        return {title:'General System Settings', category: null};
    }
  };


  useEffect(() => {
    if (error) {
      toast.error(error.message || 'An error occurred while loading settings.');
    }
  }, [error]);

  // Handle unsaved changes warning when navigating away
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);


  return (
    <div className="p-6 space-y-9">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{getSettingPageTitle().title}</h1>
          <div className="flex space-x-4">
            {hasUnsavedChanges && (
              <span className="text-orange-500 flex items-center">
                <Bell className="w-4 h-4 mr-1 animate-pulse" /> Unsaved changes
              </span>
            )}
            <Button onClick={() => fetchSettings()} variant="secondary" className="flex items-center"// Re-fetch to discard local changes
              disabled={loading}
            >
              Discard
            </Button>
            <Button
              onClick={() => saveSettings(getSettingPageTitle().category)} // Save only the active tab's settings
              variant="primary" className="flex items-center"
              disabled={loading || !hasUnsavedChanges}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {loading && <div className="text-center text-gray-500 dark:text-gray-400">Loading settings...</div>}
        {!loading && !error && (
          <div>
            {children}
          </div>
        )}
        {!loading && error && (
          <div className="text-red-500 text-center">Error: {error.message || 'Failed to load settings.'}</div>
        )}

    </div>
  );
};

export default SettingsPage;
